import { useRouter } from 'next/navigation';

import { trackAnalyticsEvent } from '@/app/lib/analytics';
import { Button } from '@/components/button';
import {
  EnergyType,
  type ProductID,
  CustomerType,
} from '@energy-retailer/constants';
import { NavigationCheck } from '@energy-retailer/icons';
import type { Product } from '@energy-retailer/schema';
import { useJoinFormStore } from '@/stores/join-form-store/join-form-store';
import { getPromoId } from '@/stores/join-form-store/cart';

type ProductPlanButtonsProps = {
  planId: string;
  fluxPlanId: ProductID;
  addAddress: () => void;
  hasAddress: boolean;
  selected?: boolean;
  product: Product;
  planSlug?: string;
  setAddAddressActive: (adressActive: boolean) => void;
};

const ProductPlanCardButtons = ({
  hasAddress,
  addAddress,
  setAddAddressActive,
  selected = false,
  planId,
  planSlug,
  fluxPlanId,
  product,
}: ProductPlanButtonsProps) => {
  const {
    fluxId: cartFluxId,
    setElectricityPlan,
    setGasPlan,
    clearGasPlan,
    clearElectricityPlan,
    setFluxId,
    setGasFluxId,
    gasAvailable,
    electricityAvailable,
    steps,
    getCurrentStep,
    currentStepNumber,
    nextStep,
    property: { address, customerType },
    promotionId,
  } = useJoinFormStore();
  const currentStep = getCurrentStep();

  const router = useRouter();

  // if we're on Electricity, each plan will have a unique fluxId assigned at render
  // if we're on Gas, we need to use the fluxId from the Electricity plan (saved in state)
  const fluxId =
    currentStep?.fuel === EnergyType.Electricity
      ? getPromoId(fluxPlanId, promotionId)
      : cartFluxId;

  const removePlan = () => {
    if (currentStep?.fuel === EnergyType.Electricity) {
      clearElectricityPlan();
      setFluxId({ productId: null });
    }

    if (currentStep?.fuel === EnergyType.Gas) {
      clearGasPlan();
      setGasFluxId({ productId: null });
    }
  };

  const selectPlan = () => {
    if (currentStep?.fuel === EnergyType.Electricity) {
      setElectricityPlan({
        planId,
        planSlug: planSlug ?? null,
        planName: product.name,
      });
      setFluxId({ productId: fluxPlanId, campaignId: promotionId });
    } else if (currentStep?.fuel === EnergyType.Gas) {
      setGasPlan({
        planId,
        planSlug: planSlug ?? null,
        planName: product.name,
      });
      setGasFluxId({ productId: fluxPlanId, campaignId: promotionId });
    }

    trackAnalyticsEvent('planSelected', {
      postcode: address.postcode,
      planName: product.name,
      planID: String(fluxId),
    });

    if (currentStepNumber === 0 && steps.length > 1) {
      nextStep();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (
      steps.length === 1 &&
      currentStep?.fuel === EnergyType.Electricity &&
      customerType === CustomerType.Residential &&
      gasAvailable
    ) {
      router.push(`/modals/gas-cross-sell`, { scroll: false });
    } else if (
      steps.length === 1 &&
      currentStep?.fuel === EnergyType.Gas &&
      customerType === CustomerType.Residential &&
      electricityAvailable
    ) {
      router.push('/modals/electricity-cross-sell', { scroll: false });
    } else {
      router.push('/sign-up/details');
    }
  };

  if (selected) {
    return (
      <div className="flex gap-a">
        <div className="flex max-w-[3.25rem] grow items-center justify-center rounded-rad-a bg-blue-500 p-a">
          <NavigationCheck className="size-icon text-white" />
        </div>
        <Button
          className="grow"
          variant="secondary"
          onClick={removePlan}
        >
          Remove Plan
        </Button>
      </div>
    );
  }

  if (hasAddress) {
    return (
      <Button
        onClick={() => selectPlan()}
        className="w-full"
      >
        Select Plan
      </Button>
    );
  }

  return (
    <Button
      onClick={() => {
        addAddress();
        setAddAddressActive(true);
      }}
    >
      Add Address
    </Button>
  );
};

export { ProductPlanCardButtons };
